/**
 * ResourceDetailActions — the `detail.actions` action group (#1417).
 *
 * Renders resource-declared `detail.actions` as footer buttons over the SAME
 * runner (`useRowActionRunner`) the row-actions dropdown uses — one pipeline,
 * one busy registry. A running action on this row disables the footer AND the
 * row's table cell (mutual exclusion). On a successful run, an action whose
 * `afterAction` is `close` leaves the panel: the runner calls `leaveDetail`
 * and refreshes without reading the record's detail again, as the action
 * may have removed it (#2802). The default (`refetch`) keeps it open,
 * relying on the runner's cache invalidation.
 *
 * A group with no runnable actions (no grant / not eligible) renders nothing.
 *
 * REQUIRES a `RowBusyProvider` ancestor (the runner reads/claims the shared
 * per-row busy registry). `ResourcePage` (drawer) and `ResourceDetailPage`
 * (standalone) both supply one; a bespoke host of `ResourceDetailPanel` with
 * `detail.actions` must wrap it in a `RowBusyProvider` too.
 */
import { useResourceKit } from '@resource-kit/core/react';
import { Button } from '../primitives/Button';
import type { ActionDefinition } from '@resource-kit/controllers/react';
import { useRowActionRunner } from './useRowActionRunner';

interface ResourceDetailActionsProps<T> {
  row: T;
  /** Stable business id for the row — the key into the shared busy registry. */
  rowId: string;
  slug: string;
  actions: ActionDefinition<T>[];
  /** Leaves the detail surface. The runner calls it on a successful run of
   *  an action whose `afterAction` is `close`. */
  leaveDetail?: () => void;
}

export function ResourceDetailActions<T>({
  row,
  rowId,
  slug,
  actions,
  leaveDetail,
}: ResourceDetailActionsProps<T>) {
  const { t } = useResourceKit();
  const { run, filterVisible, isBusy, confirmModal } = useRowActionRunner<T>({
    row,
    rowId,
    slug,
    leaveDetail,
  });

  const visible = filterVisible(actions);
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-wrap justify-end gap-2" aria-busy={isBusy}>
      {visible.map((action) => (
        <Button
          key={action.id}
          size="sm"
          variant={action.intent === 'danger' ? 'destructive' : 'outline'}
          disabled={isBusy}
          aria-busy={isBusy}
          onClick={() => {
            void run(action);
          }}
        >
          {t(action.label, { defaultValue: action.label })}
        </Button>
      ))}
      {confirmModal}
    </div>
  );
}
